/* MARCA | 2026-08-20 | Enhanced Search Modal custom element – handles open/close,
   keyboard accessibility, predictive suggestion extraction, and suppression of the
   standard search modal when enhanced mode is active. */

class EnhancedSearchModal extends HTMLElement {
  constructor() {
    super();

    this.overlay = this.querySelector('.enhanced-search-modal__overlay');
    this.input = this.querySelector('.enhanced-search-modal__input');
    this.closeBtn = this.querySelector('[data-enhanced-search-close]');
    this.pillsGrid = this.querySelector('[data-enhanced-pills-grid]');
    this.suggestionsContainer = this.querySelector('[data-enhanced-suggestions]');
    this.suggestionsList = this.querySelector('[data-enhanced-suggestions-list]');
    this.predictiveSearchEl = this.querySelector('predictive-search');
    this.predictiveResultsDiv = this.querySelector('[data-predictive-search]');
    this.isOpen = false;
    this.searchTerm = '';
    this.debounceTimer = null;
    this.cachedResults = {};

    this.bindEvents();
  }

  bindEvents() {
    this.overlay.addEventListener('click', this.close.bind(this));
    this.closeBtn.addEventListener('click', this.close.bind(this));

    /* MARCA | 2026-08-20 | Close when clicking anywhere outside the container */
    this.addEventListener('click', (e) => {
      if (e.target === this || e.target === this.overlay) this.close();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) this.close();
    });

    if (this.input) {
      this.input.addEventListener('input', this.onInputChange.bind(this));
      this.input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && this.input.value.trim().length > 0) return;
        if (e.key === 'Enter') e.preventDefault();
      });
    }

    const resetBtn = this.querySelector('.enhanced-search-modal__btn--reset');
    if (resetBtn) {
      resetBtn.addEventListener('click', this.onReset.bind(this));
    }

    document.addEventListener('enhanced-search:open', this.open.bind(this));

    /* MARCA | 2026-08-20 | Observe changes to the predictive search results container
       to extract query suggestions when the built-in predictive-search element updates */
    if (this.predictiveResultsDiv) {
      this.resultsObserver = new MutationObserver(() => {
        this.extractSuggestions();
      });
      this.resultsObserver.observe(this.predictiveResultsDiv, { childList: true, subtree: true });
    }
  }

  open() {
    this.setAttribute('open', '');
    this.setAttribute('aria-hidden', 'false');
    this.isOpen = true;
    document.body.classList.add('enhanced-search-modal--open');

    requestAnimationFrame(() => {
      if (this.input) this.input.focus();
    });

    if (typeof setScrollbarWidth === 'function') setScrollbarWidth();
  }

  close() {
    this.removeAttribute('open');
    this.setAttribute('aria-hidden', 'true');
    this.isOpen = false;
    document.body.classList.remove('enhanced-search-modal--open');

    if (this.input) this.input.value = '';
    this.clearSuggestions();

    if (this.predictiveSearchEl) {
      this.predictiveSearchEl.removeAttribute('open');
      this.predictiveSearchEl.removeAttribute('loading');
    }

    document.dispatchEvent(new CustomEvent('enhanced-search:closed'));
  }

  onInputChange() {
    const value = this.input.value.trim();
    if (value.length === 0) {
      this.clearSuggestions();
    }
  }

  onReset(e) {
    e.preventDefault();
    if (this.input) {
      this.input.value = '';
      this.input.focus();
    }
    this.clearSuggestions();

    if (this.predictiveSearchEl) {
      this.predictiveSearchEl.removeAttribute('open');
      this.predictiveSearchEl.removeAttribute('results');
    }
  }

  /* MARCA | 2026-08-20 | Extract query suggestions from the predictive search results HTML
     and render them in the dedicated suggestions column */
  extractSuggestions() {
    if (!this.predictiveResultsDiv || !this.suggestionsList) return;

    const queriesList = this.predictiveResultsDiv.querySelector('#predictive-search-results-queries-list');
    if (!queriesList) {
      return;
    }

    const items = queriesList.querySelectorAll('.predictive-search__list-item');
    if (items.length === 0) {
      this.clearSuggestions();
      return;
    }

    this.suggestionsList.innerHTML = '';

    items.forEach((item) => {
      const link = item.querySelector('a');
      if (!link) return;

      const li = document.createElement('li');
      li.className = 'enhanced-search-modal__suggestion-item';

      const a = document.createElement('a');
      a.href = link.href;
      a.className = 'enhanced-search-modal__suggestion-link';
      a.innerHTML = link.querySelector('.predictive-search__item-heading')?.innerHTML || link.textContent;
      
      li.appendChild(a);
      this.suggestionsList.appendChild(li);
    });

    this.suggestionsContainer.classList.add('has-results');
  }

  clearSuggestions() {
    if (this.suggestionsList) {
      this.suggestionsList.innerHTML = '';
    }
    if (this.suggestionsContainer) {
      this.suggestionsContainer.classList.remove('has-results');
    }
  }
}

customElements.define('enhanced-search-modal', EnhancedSearchModal);
